import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApiClient from '../services/api';
import GoogleDocsService from '../services/googleDocsService';
import { useAuth } from '../context/AuthContext';
import LanguageSelect from '../components/LanguageSelect';

const defaultDueDate = () => new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

const createPairId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const createEmptyPair = (overrides = {}) => ({
  id: createPairId(),
  originalFile: null,
  translatedDocx: null,
  sourceLanguage: 'en',
  targetLanguage: 'fr',
  requestedOriginalFileName: '',
  requestedTranslatedDocxFileName: '',
  staged: false,
  locked: false,
  ...overrides
});

const pairHasStagedFiles = (pair) => Boolean(
  pair?.originalFileName
  && pair?.translatedDocxFileName
  && pair?.hasStagedFiles !== false
);

const getFileNameFromPath = (path) => {
  if (!path) return '';
  return String(path).split(/[\\/]/).filter(Boolean).pop() || '';
};

const getDraftPairs = (draft) => {
  if (Array.isArray(draft?.pairs) && draft.pairs.length > 0) {
    return draft.pairs;
  }

  if (!draft) return [];

  return [{
    sourceLanguage: draft.sourceLanguage,
    targetLanguage: draft.targetLanguage,
    originalFileName: draft.originalFileName,
    translatedDocxFileName: draft.translatedDocxFileName,
    hasStagedFiles: draft.hasStagedFiles,
    requestedOriginalFile: draft.metadata?.requestedOriginalFile,
    requestedTranslatedDocx: draft.metadata?.requestedTranslatedDocx
  }];
};

const ImportTranslation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const importSessionId = searchParams.get('session');
  const apiClient = useApiClient();
  const {
    isGoogleAuthenticated,
    googleAccessToken,
    googleLogin,
    isGoogleLoading
  } = useAuth();

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingDraft, setLoadingDraft] = useState(Boolean(importSessionId));
  const [importDraft, setImportDraft] = useState(null);
  const [projectMode, setProjectMode] = useState('new');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [processing, setProcessing] = useState(false);
  const [currentAction, setCurrentAction] = useState('');
  const [draftError, setDraftError] = useState('');
  const [importError, setImportError] = useState('');
  const [translationPairs, setTranslationPairs] = useState([createEmptyPair()]);
  const [formData, setFormData] = useState({
    projectName: '',
    projectDescription: '',
    clientName: '',
    translatorName: '',
    country: '',
    dueDate: defaultDueDate(),
    sourceLanguage: 'en',
    targetLanguage: 'fr'
  });

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await apiClient.get('/projects');
        setProjects(response.data.projects || []);
      } catch (error) {
        console.error('Error loading projects for import:', error);
        toast.error('Failed to load projects');
      } finally {
        setLoadingProjects(false);
      }
    };

    loadProjects();
  }, [apiClient]);

  useEffect(() => {
    if (!importSessionId) {
      setLoadingDraft(false);
      return;
    }

    const blobToFile = (blob, fileName) => new File([blob], fileName, { type: blob.type || 'application/octet-stream' });

    const loadDraft = async () => {
      try {
        setLoadingDraft(true);
        setCurrentAction('Loading Claude import draft...');
        setDraftError('');

        const draftResponse = await apiClient.get(`/translations/import-drafts/${importSessionId}`);
        const draft = draftResponse.data.draft;

        setImportDraft(draft);
        setProjectMode(draft.projectId ? 'existing' : 'new');
        setSelectedProjectId(draft.projectId || '');
        setFormData((prev) => ({
          ...prev,
          projectName: draft.projectName || prev.projectName,
          projectDescription: draft.projectDescription || '',
          clientName: draft.clientName || '',
          translatorName: draft.translatorName || '',
          country: draft.country || '',
          dueDate: draft.dueDate || prev.dueDate,
          sourceLanguage: draft.sourceLanguage || prev.sourceLanguage,
          targetLanguage: draft.targetLanguage || prev.targetLanguage
        }));

        const loadedPairs = await Promise.all(getDraftPairs(draft).map(async (draftPair, index) => {
          const basePair = createEmptyPair({
            sourceLanguage: draftPair.sourceLanguage || draft.sourceLanguage || 'en',
            targetLanguage: draftPair.targetLanguage || draft.targetLanguage || 'fr',
            requestedOriginalFileName: getFileNameFromPath(draftPair.requestedOriginalFile),
            requestedTranslatedDocxFileName: getFileNameFromPath(draftPair.requestedTranslatedDocx),
            staged: pairHasStagedFiles(draftPair),
            locked: pairHasStagedFiles(draftPair)
          });

          if (!pairHasStagedFiles(draftPair)) {
            return basePair;
          }

          const [originalResponse, translatedResponse] = await Promise.all([
            apiClient.get(`/translations/import-drafts/${importSessionId}/files/original?pairIndex=${index}`, { responseType: 'blob' }),
            apiClient.get(`/translations/import-drafts/${importSessionId}/files/translated-docx?pairIndex=${index}`, { responseType: 'blob' })
          ]);

          return {
            ...basePair,
            originalFile: blobToFile(originalResponse.data, draftPair.originalFileName),
            translatedDocx: blobToFile(translatedResponse.data, draftPair.translatedDocxFileName)
          };
        }));

        setTranslationPairs(loadedPairs.length > 0 ? loadedPairs : [createEmptyPair()]);

        if (draft.status === 'COMMITTED') {
          toast.info('This Claude import draft has already been completed.');
        } else if (draft.status === 'AWAITING_FILES') {
          toast.success('Claude upload session loaded');
        } else {
          toast.success('Claude import draft loaded');
        }
      } catch (error) {
        console.error('Error loading import draft:', error);
        const message = error.response?.data?.error || 'Failed to load Claude import draft';
        setDraftError(message);
        toast.error(message);
      } finally {
        setLoadingDraft(false);
        setCurrentAction('');
      }
    };

    loadDraft();
  }, [apiClient, importSessionId]);

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedProjectId) || null,
    [projects, selectedProjectId]
  );

  useEffect(() => {
    if (!selectedProject || projectMode !== 'existing') return;

    setFormData((prev) => ({
      ...prev,
      sourceLanguage: selectedProject.sourceLanguage || prev.sourceLanguage,
      targetLanguage: selectedProject.targetLanguage || prev.targetLanguage
    }));
    setTranslationPairs((prev) => prev.map((pair) => ({
      ...pair,
      sourceLanguage: selectedProject.sourceLanguage || pair.sourceLanguage,
      targetLanguage: selectedProject.targetLanguage || pair.targetLanguage
    })));
  }, [selectedProject, projectMode]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const updatePair = (pairId, updates) => {
    setTranslationPairs((prev) => prev.map((pair) => (
      pair.id === pairId ? { ...pair, ...updates } : pair
    )));
  };

  const handlePairFileChange = (pairId, field, event) => {
    const file = event.target.files?.[0] || null;

    if (field === 'translatedDocx' && file && !file.name.toLowerCase().endsWith('.docx')) {
      toast.error('Translated file must be a DOCX file');
      event.target.value = '';
      updatePair(pairId, { translatedDocx: null });
      return;
    }

    updatePair(pairId, { [field]: file });
  };

  const addPair = () => {
    setTranslationPairs((prev) => ([
      ...prev,
      createEmptyPair({
        sourceLanguage: formData.sourceLanguage,
        targetLanguage: formData.targetLanguage
      })
    ]));
  };

  const removePair = (pairId) => {
    setTranslationPairs((prev) => prev.length === 1 ? prev : prev.filter((pair) => pair.id !== pairId));
  };

  const validate = () => {
    if (projectMode === 'existing' && !selectedProjectId) {
      toast.error('Select a project');
      return false;
    }

    if (projectMode === 'new' && !formData.projectName.trim()) {
      toast.error('Enter a project name');
      return false;
    }

    if (translationPairs.length === 0) {
      toast.error('Add at least one translation pair');
      return false;
    }

    for (const [index, pair] of translationPairs.entries()) {
      if (!pair.sourceLanguage || !pair.targetLanguage) {
        toast.error(`Select source and target languages for pair ${index + 1}`);
        return false;
      }

      if (!pair.originalFile) {
        toast.error(`Upload the original source file for pair ${index + 1}`);
        return false;
      }

      if (!pair.translatedDocx) {
        toast.error(`Upload the translated DOCX file for pair ${index + 1}`);
        return false;
      }

      if (!pair.translatedDocx.name.toLowerCase().endsWith('.docx')) {
        toast.error(`Translated file for pair ${index + 1} must be a DOCX file`);
        return false;
      }
    }

    if (!isGoogleAuthenticated || !googleAccessToken) {
      toast.error('Connect Google first so the translated DOCX can be imported to Google Docs');
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    let action = '';

    try {
      setProcessing(true);
      setImportError('');
      action = `Creating Google Docs for ${translationPairs.length} translation pair${translationPairs.length === 1 ? '' : 's'}...`;
      setCurrentAction(action);

      const googleDocs = new GoogleDocsService(googleAccessToken);
      const googleDocsByPair = [];

      for (const [index, pair] of translationPairs.entries()) {
        setCurrentAction(`Creating Google Doc ${index + 1} of ${translationPairs.length}...`);
        const fileBaseName = pair.translatedDocx.name.replace(/\.[^/.]+$/, '');
        const googleDocTitle = `Imported Translation - ${fileBaseName} (${pair.sourceLanguage.toUpperCase()} to ${pair.targetLanguage.toUpperCase()})`;
        const googleDoc = await googleDocs.importDocx(pair.translatedDocx, googleDocTitle);
        googleDocsByPair.push(googleDoc);
      }

      action = 'Saving completed translation in the platform...';
      setCurrentAction(action);

      const payload = new FormData();
      payload.append('importSource', importDraft?.importSource || 'manual_upload');
      if (importSessionId) {
        payload.append('importSessionId', importSessionId);
      }

      const pairPayload = translationPairs.map((pair, index) => {
        const googleDoc = googleDocsByPair[index];
        payload.append('originalFiles', pair.originalFile);
        payload.append('translatedDocxs', pair.translatedDocx);

        return {
          sourceLanguage: pair.sourceLanguage,
          targetLanguage: pair.targetLanguage,
          googleDocId: googleDoc.documentId,
          importMetadata: {
            googleDocumentUrl: googleDoc.documentUrl,
            googleViewUrl: googleDoc.viewUrl,
            importedVia: importDraft ? 'claude_connector_draft' : 'translator_dashboard',
            importSessionId: importSessionId || null,
            pairIndex: index
          }
        };
      });

      payload.append('translationPairs', JSON.stringify(pairPayload));

      if (projectMode === 'existing') {
        payload.append('projectId', selectedProjectId);
      } else {
        payload.append('projectName', formData.projectName.trim());
        payload.append('projectDescription', formData.projectDescription.trim());
        payload.append('clientName', formData.clientName.trim());
        payload.append('translatorName', formData.translatorName.trim());
        payload.append('country', formData.country);
        payload.append('dueDate', formData.dueDate);
      }

      const response = await apiClient.post('/translations/import-completed', payload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const firstTranslation = response.data.translations?.[0] || response.data.translation;
      const importedProjectId = firstTranslation?.projectId || firstTranslation?.project?.id;
      toast.success(`${translationPairs.length} translation${translationPairs.length === 1 ? '' : 's'} imported and marked ready for approval`);

      if (importedProjectId) {
        navigate(`/project/${importedProjectId}`);
      } else {
        navigate('/translator/dashboard');
      }
    } catch (error) {
      console.error('Error importing completed translation:', error);
      const baseMessage = error.response?.data?.error || error.message || 'Failed to import translation';
      const message = action.startsWith('Creating Google Doc')
        ? `Google Docs import failed: ${baseMessage}`
        : baseMessage;
      setImportError(message);
      toast.error(message);
    } finally {
      setProcessing(false);
      setCurrentAction('');
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6 sm:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800 mb-2">Import Translation</h1>
        <p className="text-gray-600">
          Add completed off-platform translations, create their Google Docs, and move them directly into approval and signing.
        </p>
        {importDraft && (
          <div className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
            importDraft.status === 'COMMITTED'
              ? 'border-yellow-200 bg-yellow-50 text-yellow-900'
              : 'border-blue-200 bg-blue-50 text-blue-900'
          }`}>
            {importDraft.status === 'COMMITTED'
              ? 'This Claude import draft has already been completed and cannot be imported again.'
              : (importDraft.status === 'AWAITING_FILES'
                ? 'Claude upload session loaded. Review the details, upload files, connect Google if needed, then import to create editable Google Docs.'
                : 'Claude draft loaded. Review the details, connect Google if needed, then import to create editable Google Docs.')}
          </div>
        )}
        {draftError && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {draftError}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Project</h2>
            <p className="text-sm text-gray-600">Create a new project or attach these translations to an existing one.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setProjectMode('new')}
              className={`text-left rounded-lg border p-4 transition-colors ${
                projectMode === 'new' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <span className="block font-medium text-gray-900">Create new project</span>
              <span className="block text-sm text-gray-600 mt-1">Use project details entered below.</span>
            </button>
            <button
              type="button"
              onClick={() => setProjectMode('existing')}
              className={`text-left rounded-lg border p-4 transition-colors ${
                projectMode === 'existing' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <span className="block font-medium text-gray-900">Use existing project</span>
              <span className="block text-sm text-gray-600 mt-1">Add completed translations to a current project.</span>
            </button>
          </div>

          {projectMode === 'existing' ? (
            <div>
              <label htmlFor="selectedProjectId" className="block text-sm font-medium text-gray-700 mb-2">
                Existing project *
              </label>
              <select
                id="selectedProjectId"
                value={selectedProjectId}
                onChange={(event) => setSelectedProjectId(event.target.value)}
                disabled={loadingProjects || loadingDraft}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">{loadingProjects ? 'Loading projects...' : 'Select a project'}</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name} ({project.sourceLanguage?.toUpperCase() || '?'} to {project.targetLanguage?.toUpperCase() || '?'})
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label htmlFor="projectName" className="block text-sm font-medium text-gray-700 mb-2">
                  Project name *
                </label>
                <input
                  id="projectName"
                  name="projectName"
                  value={formData.projectName}
                  onChange={handleInputChange}
                  type="text"
                  disabled={loadingDraft}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="projectDescription" className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  id="projectDescription"
                  name="projectDescription"
                  value={formData.projectDescription}
                  onChange={handleInputChange}
                  rows={3}
                  disabled={loadingDraft}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="clientName" className="block text-sm font-medium text-gray-700 mb-2">
                  Client name
                </label>
                <input
                  id="clientName"
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleInputChange}
                  type="text"
                  disabled={loadingDraft}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="translatorName" className="block text-sm font-medium text-gray-700 mb-2">
                  Project manager
                </label>
                <input
                  id="translatorName"
                  name="translatorName"
                  value={formData.translatorName}
                  onChange={handleInputChange}
                  type="text"
                  disabled={loadingDraft}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <input
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  type="text"
                  disabled={loadingDraft}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Due date
                </label>
                <input
                  id="dueDate"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleInputChange}
                  type="date"
                  min={new Date().toISOString().split('T')[0]}
                  disabled={loadingDraft}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          )}
        </section>

        <section className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Translation pairs</h2>
              <p className="text-sm text-gray-600">Each pair creates one completed translation and one editable Google Doc.</p>
            </div>
            <button
              type="button"
              onClick={addPair}
              disabled={loadingDraft || processing || importDraft?.status === 'COMMITTED'}
              className="px-4 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 disabled:opacity-50"
            >
              Add pair
            </button>
          </div>

          <div className="space-y-5">
            {translationPairs.map((pair, index) => (
              <div key={pair.id} className="rounded-lg border border-gray-200 p-4 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-medium text-gray-900">Pair {index + 1}</h3>
                  {translationPairs.length > 1 && !pair.locked && (
                    <button
                      type="button"
                      onClick={() => removePair(pair.id)}
                      disabled={processing}
                      className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor={`sourceLanguage-${pair.id}`} className="block text-sm font-medium text-gray-700 mb-2">
                      Source language *
                    </label>
                    <LanguageSelect
                      id={`sourceLanguage-${pair.id}`}
                      name="sourceLanguage"
                      value={pair.sourceLanguage}
                      onChange={(event) => updatePair(pair.id, { sourceLanguage: event.target.value })}
                      grouped={true}
                      disabled={loadingDraft || pair.locked}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor={`targetLanguage-${pair.id}`} className="block text-sm font-medium text-gray-700 mb-2">
                      Target language *
                    </label>
                    <LanguageSelect
                      id={`targetLanguage-${pair.id}`}
                      name="targetLanguage"
                      value={pair.targetLanguage}
                      onChange={(event) => updatePair(pair.id, { targetLanguage: event.target.value })}
                      grouped={true}
                      disabled={loadingDraft || pair.locked}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor={`originalFile-${pair.id}`} className="block text-sm font-medium text-gray-700 mb-2">
                      Original source file *
                    </label>
                    <input
                      id={`originalFile-${pair.id}`}
                      type="file"
                      onChange={(event) => handlePairFileChange(pair.id, 'originalFile', event)}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      disabled={loadingDraft || pair.locked}
                      className="block w-full text-sm text-gray-700 file:mr-4 file:rounded-lg file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {pair.originalFile && <p className="mt-2 text-xs text-gray-600">{pair.originalFile.name}</p>}
                    {!pair.originalFile && pair.requestedOriginalFileName && (
                      <p className="mt-2 text-xs text-gray-600">Expected: {pair.requestedOriginalFileName}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor={`translatedDocx-${pair.id}`} className="block text-sm font-medium text-gray-700 mb-2">
                      Translated DOCX *
                    </label>
                    <input
                      id={`translatedDocx-${pair.id}`}
                      type="file"
                      onChange={(event) => handlePairFileChange(pair.id, 'translatedDocx', event)}
                      accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      disabled={loadingDraft || pair.locked}
                      className="block w-full text-sm text-gray-700 file:mr-4 file:rounded-lg file:border-0 file:bg-green-50 file:px-4 file:py-2 file:text-green-800 hover:file:bg-green-100"
                    />
                    {pair.translatedDocx && <p className="mt-2 text-xs text-gray-600">{pair.translatedDocx.name}</p>}
                    {!pair.translatedDocx && pair.requestedTranslatedDocxFileName && (
                      <p className="mt-2 text-xs text-gray-600">Expected: {pair.requestedTranslatedDocxFileName}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Google Docs</h2>
              <p className="text-sm text-gray-600">
                {isGoogleAuthenticated ? 'Google is connected.' : 'Connect Google before importing translated DOCX files.'}
              </p>
            </div>
            {!isGoogleAuthenticated && (
              <button
                type="button"
                onClick={googleLogin}
                disabled={isGoogleLoading || processing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isGoogleLoading ? 'Connecting...' : 'Connect Google'}
              </button>
            )}
          </div>
        </section>

        {currentAction && (
          <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
            {currentAction}
          </div>
        )}

        {importError && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {importError}
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate('/translator/dashboard')}
            disabled={processing}
            className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={processing || loadingDraft || importDraft?.status === 'COMMITTED'}
            className="px-5 py-2 bg-green-300 rounded-lg hover:bg-green-400 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingDraft ? 'Loading draft...' : (processing ? 'Importing...' : 'Import Translations')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ImportTranslation;
